import React, { FunctionComponent, useMemo } from 'react';
import {
  Grid,
  Header,
  Segment,
  Loader,
  Container,
  Checkbox,
  Popup,
  Icon,
} from 'semantic-ui-react';
import DefaultPieChart from '../../default-pie-chart';
import { useUniqueData } from 'context/unique-data-context/unique-data-context';
import { useCallSourcesData } from 'hooks/use-call-source-data/use-call-source-data';
import {
  getCallSourceDisplayConfig,
  getOrderedCallSources,
  CALL_SOURCE_COLORS,
} from 'pages/dashboard/utils/call-source-display';

interface ContactSourceStatsProps {
  after?: Date;
  before?: Date;
  bilikZoneIds?: number[];
  proViewIds?: number[];
  solicitationCount: number | undefined;
  solicitationLoading: boolean;
}

export const ContactSourceStats: FunctionComponent<ContactSourceStatsProps> = ({
  after,
  before,
  bilikZoneIds,
  proViewIds,
  solicitationCount,
  solicitationLoading,
}) => {
  const { showUniqueData, setShowUniqueData } = useUniqueData();

  const {
    loading: callSourcesLoading,
    aggregateData: callSourceStats,
    availableSources,
  } = useCallSourcesData({
    after,
    before,
    bilikZoneIds,
    proViewIds,
    unique: showUniqueData,
    mode: 'aggregate',
  });

  const hasMailCount = Boolean(solicitationCount && solicitationCount > 0);

  const orderedSources = useMemo(() => {
    return getOrderedCallSources(availableSources, hasMailCount);
  }, [availableSources, hasMailCount]);

  const sourceColorConfig = useMemo(() => {
    return getCallSourceDisplayConfig(availableSources, hasMailCount);
  }, [availableSources, hasMailCount]);

  const webSources = useMemo(() => {
    return availableSources
      .filter((source) => source.toLowerCase().includes('web'))
      .reduce((sum, source) => sum + Number(callSourceStats?.[source] || 0), 0);
  }, [availableSources, callSourceStats]);

  const guideSources = useMemo(() => {
    return availableSources
      .filter((source) => !source.toLowerCase().includes('web'))
      .reduce((sum, source) => sum + Number(callSourceStats?.[source] || 0), 0);
  }, [availableSources, callSourceStats]);

  const webVsGuidePieChartData = useMemo(() => {
    if (callSourcesLoading || solicitationLoading) return [];

    const totalWebSources = webSources + (solicitationCount || 0);
    const totalGuideSources = guideSources;

    const data: Array<{ id: string; label: string; value: number }> = [];

    if (totalWebSources > 0) {
      data.push({
        id: 'web',
        label: `Web (${totalWebSources})`,
        value: totalWebSources,
      });
    }

    if (totalGuideSources > 0) {
      data.push({
        id: 'guide',
        label: `Guide (${totalGuideSources})`,
        value: totalGuideSources,
      });
    }

    return data;
  }, [
    webSources,
    guideSources,
    solicitationCount,
    callSourcesLoading,
    solicitationLoading,
  ]);

  const webVsGuideColorScheme = useMemo(() => {
    return webVsGuidePieChartData.map((item) =>
      item.id === 'web' ? CALL_SOURCE_COLORS.web : CALL_SOURCE_COLORS.guide,
    );
  }, [webVsGuidePieChartData]);

  const detailedPieChartData = useMemo(() => {
    if (callSourcesLoading || solicitationLoading) return [];

    const data: Array<{
      id: string;
      label: string;
      value: number;
      color: string;
    }> = [];

    orderedSources.forEach((source) => {
      let value: number;

      if (source === 'mail') {
        value = solicitationCount || 0;
      } else {
        value = Number(callSourceStats?.[source] || 0);
      }

      if (value > 0) {
        const config = sourceColorConfig[source];
        data.push({
          id: source,
          label: `${config?.label || source} (${value})`,
          value: value,
          color: config?.color || '#ccc',
        });
      }
    });

    return data;
  }, [
    orderedSources,
    callSourceStats,
    sourceColorConfig,
    solicitationCount,
    callSourcesLoading,
    solicitationLoading,
  ]);

  const detailedColorScheme = useMemo(() => {
    return detailedPieChartData.map((item) => item.color);
  }, [detailedPieChartData]);

  const isLoading = callSourcesLoading || solicitationLoading;

  return (
    <>
      <Segment>
        <Header>Sources des contacts vers les pros</Header>
      </Segment>
      <Segment>
        <Container style={{ textAlign: 'right', marginTop: -42 }}>
          <Checkbox
            label="Unique"
            checked={showUniqueData}
            onChange={() => setShowUniqueData(!showUniqueData)}
            toggle
          />
          <Popup
            position="left center"
            trigger={
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: 5,
                  paddingBottom: 10,
                }}
              >
                <Icon name="info circle" />
              </div>
            }
            content="Compte seulement les contacts uniques par pro et par canal (appel/mail). Ex: Si un particulier contacte un pro 3 fois par téléphone, puis un second 2 fois par email, il ne sera compté que 2 fois."
          />
        </Container>
        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column width={8}>
              {!isLoading ? (
                <DefaultPieChart
                  data={webVsGuidePieChartData}
                  colorScheme={webVsGuideColorScheme}
                  showPercentage
                />
              ) : (
                <div
                  style={{
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loader active size="large" />
                </div>
              )}
            </Grid.Column>
            <Grid.Column width={8}>
              {!isLoading ? (
                <DefaultPieChart
                  data={detailedPieChartData}
                  colorScheme={detailedColorScheme}
                  showPercentage
                />
              ) : (
                <div
                  style={{
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loader active size="large" />
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </>
  );
};

export default ContactSourceStats;
